import apiClient from './apiClient';
import logger from '../utils/logger';

const ADMIN_BASE = '/api/admin';

/**
 * Phase 8 — read-only audit log feed. All filters optional; pass ISO date
 * strings for from/to. limit clamped server-side (default 100, max 500).
 */
export const getAuditLog = async ({
  actorUserId = null,
  targetType = null,
  targetId = null,
  from = null,
  to = null,
  limit = 100,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (actorUserId) params.set('actorUserId', actorUserId);
    if (targetType) params.set('targetType', targetType);
    if (targetId) params.set('targetId', targetId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (limit) params.set('limit', String(limit));
    const { data } = await apiClient.get(`${ADMIN_BASE}/audit?${params.toString()}`);
    return data;
  } catch (error) {
    logger.error('getAuditLog failed:', error);
    throw error;
  }
};

/** Funnel + user counter-card data for the /admin dashboard. */
export const getStatsOverview = async () => {
  try {
    const { data } = await apiClient.get(`${ADMIN_BASE}/stats/overview`);
    return data;
  } catch (error) {
    logger.error('getStatsOverview failed:', error);
    throw error;
  }
};

/** Paginated user list. status ∈ {paid|free|null}; role e.g. "Admin", "User". */
export const getUsers = async ({ query = '', status = null, role = null, page = 1, pageSize = 25 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (query) params.set('query', query);
    if (status) params.set('status', status);
    if (role) params.set('role', role);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const { data } = await apiClient.get(`${ADMIN_BASE}/users?${params.toString()}`);
    return data;
  } catch (error) {
    logger.error('getUsers failed:', error);
    throw error;
  }
};

/** Paginated lead list. status filter optional. */
export const getInviteRequests = async ({ status = null, page = 1, pageSize = 25 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const { data } = await apiClient.get(`${ADMIN_BASE}/invites?${params.toString()}`);
    return data;
  } catch (error) {
    logger.error('getInviteRequests failed:', error);
    throw error;
  }
};

/** Status transition for a lead row. */
export const updateInviteStatus = async (id, status) => {
  try {
    const { data } = await apiClient.patch(`${ADMIN_BASE}/invites/${id}/status`, { Status: status });
    return data;
  } catch (error) {
    logger.error('updateInviteStatus failed:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
//   Phase 3 — user management actions
//   Each one expects a non-empty reason; the BE rejects shorter than 4 chars.
// ---------------------------------------------------------------------------

/** Mark a user's email confirmed. Idempotent. */
export const adminConfirmUserEmail = async (userId, { reason } = {}) => {
  try {
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/users/${userId}/confirm-email`,
      { Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminConfirmUserEmail failed:', error);
    throw error;
  }
};

/** Trigger a reset-password email for the user (same flow as /forgot-password). */
export const adminSendResetPassword = async (userId, { reason } = {}) => {
  try {
    const clientUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/users/${userId}/send-reset-password`,
      { Reason: reason, ClientUrl: clientUrl },
    );
    return data;
  } catch (error) {
    logger.error('adminSendResetPassword failed:', error);
    throw error;
  }
};

/** Replace a user's roles. BE rejects unknown roles, self-demotion, last-admin demotion. */
export const adminUpdateUserRoles = async (userId, { roles, reason } = {}) => {
  try {
    const { data } = await apiClient.put(
      `${ADMIN_BASE}/users/${userId}/roles`,
      { Roles: roles, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminUpdateUserRoles failed:', error);
    throw error;
  }
};

/** Agent-on-behalf submission. Same fields as the visitor modal but tagged call_center. */
export const agentSubmitInvite = async ({ email, phone, name, request }) => {
  try {
    const body = {
      Email: (email || '').trim(),
      Phone: phone?.trim() || null,
      Name: name?.trim() || null,
      Request: (request || '').trim(),
    };
    const { data } = await apiClient.post(`${ADMIN_BASE}/invites/request`, body);
    return data;
  } catch (error) {
    logger.error('agentSubmitInvite failed:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
//   Phase 6 — lead (InviteRequest) management actions
//   Each one expects a non-empty reason; the BE rejects shorter than 4 chars.
// ---------------------------------------------------------------------------

/** Full edit of a lead's identity/free-text fields. */
export const adminEditLead = async (
  id,
  { email, phone, name, request, utmSource, notes, reason } = {},
) => {
  try {
    const body = {
      Email: email !== undefined ? (email?.trim() || null) : undefined,
      Phone: phone !== undefined ? (phone?.trim() || null) : undefined,
      Name: name !== undefined ? (name?.trim() || null) : undefined,
      Request: request !== undefined ? (request?.trim() || null) : undefined,
      UtmSource: utmSource !== undefined ? (utmSource?.trim() || null) : undefined,
      Notes: notes !== undefined ? (notes?.trim() || null) : undefined,
      Reason: reason,
    };
    const { data } = await apiClient.put(`${ADMIN_BASE}/invites/${id}`, body);
    return data;
  } catch (error) {
    logger.error('adminEditLead failed:', error);
    throw error;
  }
};

/** Assign (or unassign with null) an admin/CRM agent to a lead. */
export const adminAssignLeadAgent = async (id, { agentId, reason } = {}) => {
  try {
    const { data } = await apiClient.patch(
      `${ADMIN_BASE}/invites/${id}/agent`,
      { AgentId: agentId || null, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminAssignLeadAgent failed:', error);
    throw error;
  }
};

/** Replace or append-with-timestamp the lead's internal notes. */
export const adminEditLeadNotes = async (
  id,
  { notes, mode = 'append', reason } = {},
) => {
  try {
    const { data } = await apiClient.patch(
      `${ADMIN_BASE}/invites/${id}/notes`,
      { Notes: notes, Mode: mode, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminEditLeadNotes failed:', error);
    throw error;
  }
};

/** Mark the lead "converted" and link it to a registered User id. */
export const adminConvertLead = async (id, { linkedUserId, reason } = {}) => {
  try {
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/invites/${id}/convert`,
      { LinkedUserId: linkedUserId, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminConvertLead failed:', error);
    throw error;
  }
};

/** Re-send the invitation email to the lead (same template as the auto-reply). */
export const adminResendLead = async (id, { reason } = {}) => {
  try {
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/invites/${id}/resend`,
      { Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminResendLead failed:', error);
    throw error;
  }
};

/**
 * Update lead status WITH an explicit reason — Phase 6 replacement for the
 * legacy `updateInviteStatus(id, status)` shorthand. The BE still accepts the
 * old body shape (no reason) for backward compat, but new callers must include
 * a reason so the audit row is meaningful.
 */
export const adminUpdateLeadStatus = async (id, { status, reason } = {}) => {
  try {
    const { data } = await apiClient.patch(
      `${ADMIN_BASE}/invites/${id}/status`,
      { Status: status, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminUpdateLeadStatus failed:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
//   Phase 7 — bulk lead operations + CSV-style import
//   Each endpoint accepts 1..100 ids/rows per call and a reason ≥ 4 chars.
//   Response shape: { success, total, succeeded, failed, notFound, items[] }
// ---------------------------------------------------------------------------

/** Apply the same status transition to up to 100 lead ids in one call. */
export const adminBulkUpdateLeadStatus = async ({ ids, status, reason } = {}) => {
  try {
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/invites/bulk/status`,
      { Ids: ids, Status: status, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminBulkUpdateLeadStatus failed:', error);
    throw error;
  }
};

/** Assign (or unassign with null) the same agent to up to 100 lead ids. */
export const adminBulkAssignLeadAgent = async ({ ids, agentId, reason } = {}) => {
  try {
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/invites/bulk/agent`,
      { Ids: ids, AgentId: agentId || null, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminBulkAssignLeadAgent failed:', error);
    throw error;
  }
};

/**
 * Insert up to 100 lead rows in one call (CSV import).
 * If includeEmail is true, the BE sends the invitation email per row;
 * otherwise the rows are inserted silently.
 */
export const adminBulkImportLeads = async ({
  rows,
  reason,
  source = null,
  includeEmail = false,
} = {}) => {
  try {
    const body = {
      Rows: rows,
      Reason: reason,
      IncludeEmail: !!includeEmail,
    };
    if (source) body.Source = source;
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/invites/bulk/import`,
      body,
    );
    return data;
  } catch (error) {
    logger.error('adminBulkImportLeads failed:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
//   Phase 4 + 5 — credits / subscription / deactivate / export / hard delete
//   Reason ≥ 4 chars on the BE for every action that takes one.
// ---------------------------------------------------------------------------

/** Add or subtract from one of the two credit buckets. Amount must be ≠ 0. */
export const adminAdjustCredits = async (userId, { amount, category, reason } = {}) => {
  try {
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/users/${userId}/credits/adjust`,
      { Amount: amount, Category: category, Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminAdjustCredits failed:', error);
    throw error;
  }
};

/** Override the user's current subscription (plan / window / payment status). */
export const adminOverrideSubscription = async (
  userId,
  { subscriptionId, startDate, endDate, paymentStatus, reason } = {},
) => {
  try {
    const body = {
      SubscriptionId: subscriptionId,
      StartDate: startDate,
      EndDate: endDate,
      Reason: reason,
    };
    if (paymentStatus) body.PaymentStatus = paymentStatus;
    const { data } = await apiClient.put(
      `${ADMIN_BASE}/users/${userId}/subscription`,
      body,
    );
    return data;
  } catch (error) {
    logger.error('adminOverrideSubscription failed:', error);
    throw error;
  }
};

/** Lock the user out at login. BE rejects self-deactivation with 400. */
export const adminDeactivateUser = async (userId, { reason } = {}) => {
  try {
    const { data } = await apiClient.post(
      `${ADMIN_BASE}/users/${userId}/deactivate`,
      { Reason: reason },
    );
    return data;
  } catch (error) {
    logger.error('adminDeactivateUser failed:', error);
    throw error;
  }
};

/**
 * GDPR export: pulls the JSON bundle for one user and triggers a browser
 * download. The BE writes the audit row itself, so callers don't need to pass
 * a reason. Returns { success: true } on a successful download.
 */
export const adminExportUserData = async (userId) => {
  try {
    const response = await apiClient.get(
      `${ADMIN_BASE}/users/${userId}/export`,
      { responseType: 'blob' },
    );

    // Try to derive a slug from the user's email if the BE included one in the
    // payload; otherwise fall back to the raw id. We parse a copy of the blob
    // as text so we don't consume the one we hand to URL.createObjectURL.
    let emailSlug = '';
    try {
      const text = await response.data.slice().text();
      const parsed = JSON.parse(text);
      const email = parsed?.user?.email || parsed?.email || '';
      emailSlug = (email || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch {
      // Ignore — slug stays empty, filename falls back to the id.
    }

    const today = new Date().toISOString().slice(0, 10);
    const filename = `go4p-user-${emailSlug || userId}-${today}.json`;

    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([JSON.stringify(response.data)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    return { success: true };
  } catch (error) {
    logger.error('adminExportUserData failed:', error);
    throw error;
  }
};

/** Hard delete a user account. BE rejects self-delete and last-admin delete. */
export const adminDeleteUser = async (userId, { reason } = {}) => {
  try {
    const { data } = await apiClient.delete(`${ADMIN_BASE}/users/${userId}`, {
      data: { Reason: reason },
    });
    return data;
  } catch (error) {
    logger.error('adminDeleteUser failed:', error);
    throw error;
  }
};
